import _ from 'lodash';
import tinycolor from 'tinycolor2';
import {
  BAR_CHART_ESSENTIALS,
  CAPSULE_TYPES,
  DISABLED_MARKER,
  ENABLED_MARKER,
  LINE_WIDTHS,
  UNCERTAIN_CAPSULE_TYPES,
} from '@/chart/chart.constants';
import { getScaledFontSize } from '@/core/html.utilities';
import { DEFAULT_EXTREMES } from '@/scatterPlot/scatterPlot.constants';
import {
  DARK_THEME,
  LANE_LABEL_CONFIG,
  LIGHT_THEME,
  PLOT_BAND_AXIS_ID,
} from '@/trend/trendViewer/trendViewer.constants';
import {
  CUSTOMIZATION_MODES,
  CapsuleTimeColorMode,
  ITEM_TYPES,
  OVERLAY_LINE_WIDTH,
  PREVIEW_HIGHLIGHT_COLOR,
  PREVIEW_ID,
  SAMPLE_OPTIONS,
} from '@/trendData/trendData.constants';
import { chartLanes } from '@/utilities/chartLanes';
import { LANE_LABEL_HEIGHT_REM } from '@/utilities/chartLanes.constants';
import { computeOverlayLayoutForLevel, getOverlayOffset } from '@/utilities/chartOverlay.utilities';
import { getCertainId } from '@/utilities/id.utilities';
import {
  fetchTickAttributes,
  formatStringYAxisLabel,
  formatYAxisTick,
  getAxisDisplayText,
  getAxisTitleOptions,
  getCapsuleAxisId,
  getLabelWidth,
  getLaneDisplayText,
  getNumericTickPositions as getNumericTickPositionsSqLabel,
} from '@/utilities/label.utilities';
import { formatNumber } from '@/utilities/numberHelper.utilities';
import { formatString } from '@/utilities/stringHelper.utilities';
import type { ConditionValuesForLabels, TrendDataForChart } from '@/annotation/interactiveContent.types';
import type {
  ChartGetters,
  ChartRegion,
  PixelTranslationFunction,
  SeriesGroupedByAxis,
  XYPixelRegion,
} from '@/chart/chart.constants';
import type { XYPlotRegion } from '@/scatterPlot/scatterPlot.constants';
import type { LabelDisplayConfiguration } from '@/trendData/trendData.constants';
import type { StringEnum } from '@/trendData/trendData.types';
import type {
  AdjustableConditions,
  ChartLaneOptions,
  ConditionAxisAndOffsets,
  ConditionLaneData,
} from '@/utilities/chart.types';
import type { FormatOptions } from '@/utilities/numberHelper.utilities';
import type { UnitOfMeasure } from '@/utilities/unit.utilities';
import type { Extremes } from '@seeqdev/highcharts';

const laneLabelHeight = () => getScaledFontSize(LANE_LABEL_HEIGHT_REM);

/**
 * Height available below the lane label for traces and y-axes. Falls back to the full lane
 * height when the lane is shorter than the label band — otherwise the difference clips to
 * zero/negative and SVG hides every trace inside the clip group.
 */
const availableLaneHeight = (laneHeight: number, labelHeight: number): number =>
  laneHeight > labelHeight ? laneHeight - labelHeight : laneHeight;

/**
 * detect incorrect regions on trend
 */
export function isInvalidPixel(capsuleRegion: XYPixelRegion) {
  const { xMinPixel, xMaxPixel } = capsuleRegion;

  return !_.isFinite(xMinPixel) || !_.isFinite(xMaxPixel) || xMinPixel >= xMaxPixel;
}

/**
 * translate the XYRegion to a pixel region (with an id and dateTime if it is a capsule)
 */
export function translateRegion(
  chart: Highcharts.Chart,
  xAxis: Highcharts.Axis,
  yAxis: Highcharts.Axis,
  translate: { x?: PixelTranslationFunction; y?: PixelTranslationFunction },
  chartRegion: ChartRegion,
): XYPixelRegion {
  const xMin = chartRegion.xMin;
  const xMax = chartRegion.xMax;
  const yMin = chartRegion.yMin;
  const yMax = chartRegion.yMax;
  const xPixels = translateAxis(xMin as number, xMax as number, xAxis, translate.x!);
  const yPixels = translateAxis(yMin as number, yMax as number, yAxis, translate.y!);

  function translateAxis(min: number, max: number, axis: Highcharts.Axis, translate: PixelTranslationFunction) {
    if (translate) {
      return translate(chart, min, max);
    }

    const extremes = axis.getExtremes();

    return {
      minPixel: axis.translate(Math.max(min, extremes.min)),
      maxPixel: axis.translate(Math.min(max, extremes.max)),
    };
  }

  return {
    xMinPixel: xPixels.minPixel as number,
    xMaxPixel: xPixels.maxPixel as number,
    yMinPixel: yPixels.minPixel as number,
    yMaxPixel: yPixels.maxPixel as number,
    id: chartRegion.id ?? '',
    dateTime: chartRegion.dateTime!,
    yValue: chartRegion.yValue,
    lane: chartRegion.lane,
  };
}

/**
 * Checks to see if the mouse has left the plot area and resets the zoomInProgress flag to reset the selection
 * marker
 *
 * @param e - The mouse event
 * @param highChart - the current chart object
 */
export function mouseLeftActualChartArea(e: MouseEvent, highChart: Highcharts.Chart | null) {
  let plotBox;
  let downXPixels;
  let downYPixels;

  if (e && highChart && highChart.pointer) {
    downXPixels = highChart.pointer.normalize(e).chartX;
    downYPixels = highChart.pointer.normalize(e).chartY;
    plotBox = highChart.plotBox;
    return (
      downYPixels <= plotBox.y ||
      downYPixels > plotBox.y + plotBox.height ||
      downXPixels < plotBox.x ||
      downXPixels > plotBox.x + plotBox.width
    );
  } else {
    return true;
  }
}

/**
 * Find the chart series object that matches the id specified
 *
 * @param highChart - the current chart object
 * @param  id - ID value for which to search
 * @return Chart series object; undefined if not found
 */
export function findChartSeries(highChart: Highcharts.Chart | null, id: string): Highcharts.Series {
  return _.find(highChart?.series as Highcharts.Series[], { options: { id } }) as Highcharts.Series;
}

/**
 * Transforms a pair of Highcharts X and Y selection objects with min and max into a matching XYRegion
 *
 * @param xAxis axis for min and max x points
 * @param yAxes axes for min and max y points
 * @return The selected region according to the axis objects
 */
export function getXYRegion(xAxis: Highcharts.SelectDataObject, yAxes: Highcharts.SelectDataObject[]): XYPlotRegion {
  const x = {
    min: xAxis.min,
    max: xAxis.max,
  };
  const ys: Record<string, Extremes> = {};
  _.forEach(yAxes, (axis) => {
    ys[axis.axis.userOptions.signalId as string] = {
      min: axis.min,
      max: axis.max,
    };
  });
  return {
    x,
    ys,
  };
}

export function setScatterPlotExtremes(xAxis: Highcharts.Axis, yAxes: Highcharts.Axis[], region: XYPlotRegion) {
  const xMin = region.x.min ?? null;
  const xMax = region.x.max ?? null;
  xAxis.setExtremes(xMin ?? undefined, xMax ?? undefined, false);
  _.forEach(yAxes, (axis) => {
    const signalId = axis.userOptions.signalId;
    const y = signalId ? region.ys[signalId] : undefined;
    const yExtremes = y ?? DEFAULT_EXTREMES;
    const yMin = yExtremes.min ?? null;
    const yMax = yExtremes.max ?? null;
    axis.setExtremes(yMin ?? undefined, yMax ?? undefined, false);
  });
}

/**
 * Sets the label visibility and layout of a given axis. Does not redraw the chart.
 *
 * @param axis - The axis label to be updated
 * @param properties - Object containing the properties and their new values. Properties are merged
 *   with existing properties or the axis.
 */
export function setAxisProperties(axis: Highcharts.Axis, properties: any) {
  if (axis) {
    axis.update(properties, false);
  }
}

/**
 * Gets the axis associated with an item
 *
 * @param chart - The highcharts chart to look at
 * @param item - The item to find
 * @param  item.id - The id of the item to find
 *
 * @returns  The requested axis; or undefined if not found.
 */
export function getItemYAxis(
  chart: Highcharts.Chart,
  item: { id: string; conditionId: string; itemType: string },
): any | undefined {
  const id = item?.itemType === ITEM_TYPES.CAPSULE ? getCapsuleAxisId(item.conditionId) : `yAxis-${item?.id}`;

  return _.find(chart.yAxis, { userOptions: { id } });
}

//region Lane Helpers

/**
 * Helper function that returns an array of unique values for the provided property ordered in the same order of
 * the possible values provided. This is useful to get all the assigned lanes as well as axis assignments.
 *
 * @param items - list of items to check for properties
 * @param property - the property to retrieve the unique assignments for.
 * @param possibleValues - the possible values the property could be
 * @returns Array of the unique values for the provided property.
 */
export function getUniqueOrderedValuesByProperty(items: any[], property: string, possibleValues: any[]): any[] {
  return _.chain(items)
    .map(property)
    .compact()
    .uniq()
    .orderBy((value) => _.indexOf(possibleValues, value))
    .value();
}

/**
 * Clip signals to their lane so they can't be dragged into other lanes (CRAB-8895)
 *
 * This works by using the plotBand highlighting to create a clipRect for the lane and using it for all signals
 * within the lane. The clipRects are actually clipPaths[1] in the svg. All the signals can share the same
 * clipRect because highcharts uses the transform attribute[2] to move the svg path into position and the
 * clipping path is transformed with the data
 *
 * [1]: https://developer.mozilla.org/en-US/docs/Web/SVG/Element/clipPath
 * [2]: https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/transform
 */
export function clipSignalsToLanes(options: {
  laneClipRects: Highcharts.ClipRectElement[];
  items: any[];
  chart: Highcharts.Chart;
  capsuleLaneHeight: number;
  isCapsuleTime: boolean;
  laneWidth: number;
  labelDisplayConfiguration: LabelDisplayConfiguration;
}): Highcharts.ClipRectElement[] {
  const {
    chart,
    items,
    isCapsuleTime,
    capsuleLaneHeight,
    laneClipRects,
    laneWidth,
    labelDisplayConfiguration: _labelDisplayConfiguration,
  } = options;
  laneClipRects?.forEach((clipRect) => clipRect.destroy?.());

  const signalLaneHeight = chartLanes.getSignalLaneHeight({ chart, items, isCapsuleTime, capsuleLaneHeight });
  const clipHeight = availableLaneHeight(signalLaneHeight, laneLabelHeight());

  return chart.series.map((chartSeries) => {
    const lane = _.get(chartSeries, 'userOptions.lane', false);
    const newLaneClipRect = chart.renderer.clipRect(0, 0, laneWidth, clipHeight);
    const isSignal = _.includes([ITEM_TYPES.SCALAR, ITEM_TYPES.SERIES], _.get(chartSeries, 'userOptions.itemType'));
    if (chartSeries.visible && lane && !_.isUndefined(chartSeries.group) && isSignal) {
      chartSeries.group.clip(newLaneClipRect);
      chartSeries.markerGroup.clip(newLaneClipRect);
    }

    return newLaneClipRect;
  });
}

/**
 * Returns an array of series organized by how their y-axes should be displayed.
 *
 * @return An array of series, organized by how they should be displayed
 *  .primarySeries - Primary series for this axis. The y-axis for this series is the one that is
 *    displayed for all items in .series.
 *  .series - All series that occupy the same axis area and should all be updated together
 *  .hide - If true, the axis shouldn't be displayed
 */
export function getSeriesForYAxisInteraction(options: { items: any[]; lane?: string | number }): SeriesGroupedByAxis[] {
  const { items, lane } = options;
  let series = _.filter(
    items,
    (item: any) =>
      item.itemType === ITEM_TYPES.SERIES ||
      item.itemType === ITEM_TYPES.SCALAR ||
      item.itemType === ITEM_TYPES.CONDITION,
  );

  if (!_.isUndefined(lane)) {
    series = _.filter(series, { lane });
  }

  if (series.length === 0) {
    return [
      {
        primarySeries: undefined,
        series,
      },
    ];
  }

  // for multiple series which have the same yAxisAlignment, ensure that we only show one axis
  const skippedSeriesIds: string[] = [];
  return _.transform(
    series,
    (accum: SeriesGroupedByAxis[], singleSeries: any, index) => {
      const remainingSeries = _.slice(series, index + 1);
      const matches = _.chain(remainingSeries)
        .filter((other) => shareSameAxis([singleSeries, other]) && shareSameLane([singleSeries, other]))
        .forEach((match: any) => skippedSeriesIds.push(match.id))
        .value();

      if (!_.includes(skippedSeriesIds, singleSeries.id)) {
        accum.push({
          primarySeries: singleSeries,
          series: _.concat([singleSeries], matches),
          hide: false,
        });
      }
    },
    [] as SeriesGroupedByAxis[],
  );
}

/**
 * Helper function to determine if all the provided Series share the same lane.
 *
 * @param seriesList - Array of series items.
 * @returns true if the series share the same lane, false if not.
 */
function shareSameLane(seriesList: any[]): boolean {
  return _.chain(seriesList).map('lane').uniq().value().length === 1;
}

/**
 * Helper function to determine if all the provided Series share the same axis.
 *
 * @param seriesList - Array of series items.
 * @returns true if the series share the same axis, false if not.
 */
function shareSameAxis(seriesList: any[]): boolean {
  return _.chain(seriesList).map('axisAlign').uniq().value().length === 1;
}

/**
 * Iterates through the y axes and pushes any necessary lane and formatting changes to the chart.
 *
 * @param options
 * @return the new plot band colors
 */
export function processYAxisChangesPerLane(options: {
  isCapsuleTime: boolean;
  isCompareViewRainbowColorMode?: boolean;
  items: any[];
  chart: Highcharts.Chart;
  capsuleLaneHeight: number;
  capsuleTimeColorMode: CapsuleTimeColorMode;
  labelDisplayConfiguration: LabelDisplayConfiguration;
  useDefaultColor?: boolean;
  darkMode?: boolean;
}): Record<number, string> {
  const {
    isCapsuleTime,
    isCompareViewRainbowColorMode,
    items,
    capsuleTimeColorMode,
    chart,
    labelDisplayConfiguration: _labelDisplayConfiguration,
    useDefaultColor = false,
    darkMode = false,
  } = options;

  let labelColor: string;
  const plotBandColors: Record<number, string> = {};
  const lanes = chartLanes.getDisplayedLanes(items);
  let opposite = false;
  const seriesByAxis = getSeriesForYAxisInteraction(options);

  _.forEach(lanes, (lane) => {
    const labelHeight = laneLabelHeight();
    const seriesInLane = _.filter(seriesByAxis, (s) => _.get(s.primarySeries, 'lane') === lane);

    const { offset, height: laneHeight, conditionHeight, signalHeight } = chartLanes.computeLaneValues(lane, options);
    // If labels are enabled or a string signal is in the first lane make room for them by pushing the Y-axis down
    // For mixed lanes (conditions + signals), use only the signal portion for axis positioning
    const effectiveHeight = signalHeight ?? laneHeight;
    const effectiveOffset = conditionHeight ? offset + conditionHeight : offset;
    const axisHeight = availableLaneHeight(effectiveHeight, labelHeight);
    const axisTop = effectiveOffset + (effectiveHeight > labelHeight ? labelHeight : 0);

    _.forEach(seriesInLane, (seriesGroup) => {
      const axis = getItemYAxis(chart, seriesGroup.primarySeries);
      let visible = seriesGroup.primarySeries.axisVisibility;

      // When updating a series, the addition of its preview series causes an update before the preview has an
      // axis
      if (_.isUndefined(axis)) {
        return;
      }

      if (seriesGroup.series.length > 1) {
        const visibleSeries = _.chain(seriesGroup.series).filter({ axisVisibility: true }).uniqBy('id').value();

        const allColorsSame =
          visibleSeries.length > 0 ? _.every(visibleSeries, ['color', visibleSeries[0]?.color]) : false;
        const hasColors =
          isCompareViewRainbowColorMode ||
          (isCapsuleTime &&
            _.includes([CapsuleTimeColorMode.Rainbow, CapsuleTimeColorMode.ConditionGradient], capsuleTimeColorMode));
        if (allColorsSame && !hasColors) {
          labelColor = visibleSeries[0].color;
        }

        const temp = _.omitBy(seriesGroup.series, (tempSeries) => tempSeries.id === seriesGroup.primarySeries.id);
        _.forEach(temp, (t) => {
          const tempAxis = getItemYAxis(chart, t);
          setAxisProperties(tempAxis, {
            visible: false,
            height: axisHeight,
            top: axisTop,
          });
        });
      } else {
        labelColor = seriesGroup.primarySeries.color;
        if (
          !_.isFinite(_.get(seriesGroup.primarySeries, 'yAxisConfig.min')) &&
          !_.isFinite(_.get(seriesGroup.primarySeries, 'yAxisConfig.max'))
        ) {
          visible = false;
        }
      }

      if (!_.isUndefined(_.find(seriesGroup.series as any[], (signal) => _.startsWith(signal.id, PREVIEW_ID)))) {
        plotBandColors[lane] = PREVIEW_HIGHLIGHT_COLOR;
      }

      opposite = seriesGroup.primarySeries.rightAxis;

      setAxisProperties(
        axis,
        _.merge({}, darkMode ? DARK_THEME.yAxis : LIGHT_THEME.yAxis, {
          visible,
          ...(labelColor && !useDefaultColor ? { lineColor: labelColor } : {}),
          lineWidth: 1,
          labels: {
            enabled: visible,
            align: opposite ? 'left' : 'right',
            x: opposite ? 5 : -5,
            style: { ...axis?.labels?.style, ...(labelColor && !useDefaultColor ? { color: labelColor } : {}) },
          },
          height: axisHeight,
          formatOptions: seriesGroup.primarySeries.formatOptions || {},
          top: axisTop,
          opposite,
          type: seriesGroup.primarySeries.yAxisType,
        }),
      );
    });
  });

  return plotBandColors;
}

/**
 * Updates the "lanes" background on the chart. The label object is defined so that the lane display can be
 * turned on/off based on the customizationMode in the trendStore.
 *
 * @param options.colors - Map of lane number to custom color for that lane
 * @param options.darkMode - Whether dark mode is enabled
 */
export function updatePlotBands(options: {
  colors: any;
  items: any[];
  isCapsuleTime: boolean;
  capsuleLaneHeight: number;
  chart: Highcharts.Chart;
  darkMode?: boolean;
}): Map<number, string> {
  const { colors, items, chart, capsuleLaneHeight } = options;
  const lanes = chartLanes.getDisplayedLanes(items);
  const laneCount = lanes.length;
  const maxHeight = getChartSeriesDisplayHeight(options);

  const plotBandColors = new Map<number, string>();
  const plotBands: Highcharts.YAxisPlotBandsOptions[] = _.flatMap(lanes, (lane, idx) => {
    const itemId = _.find(items, { lane })?.id;
    const isPreviewItem =
      _.includes(itemId, PREVIEW_ID) || _.chain(items).filter({ lane }).some({ isPreviewItem: true }).value();
    const color = colors[lane] || 'transparent';
    plotBandColors.set(lane, color);
    const { offset, height, buffer } = chartLanes.computeLaneValues(lane, options);

    return [
      {
        color,
        className: `${(laneCount - idx) % 2 === 0 ? 'even' : 'odd'}${colors[lane] ? ' custom' : ''}${
          isPreviewItem ? ' preview' : ''
        }`,
        from: maxHeight - offset + capsuleLaneHeight,
        to: maxHeight - (offset + height) + capsuleLaneHeight,
        label: {
          ...LANE_LABEL_CONFIG,
          style: {
            ...LANE_LABEL_CONFIG.style,
          },
          text: '', // Will be filled in by manageLaneLabelDisplay()
        },
      },
      {
        color: 'transparent',
        from: maxHeight - (offset + height) + capsuleLaneHeight,
        to: maxHeight - (offset + height + buffer) + capsuleLaneHeight,
      },
    ];
  });

  const axis = _.find(chart.yAxis, {
    userOptions: { id: PLOT_BAND_AXIS_ID },
  });

  if (!axis) {
    return plotBandColors;
  }

  axis.update(
    {
      plotBands,
      visible: true,
      min: 0,
      max: maxHeight,
      height: maxHeight,
      top: capsuleLaneHeight,
    },
    false,
  );

  return plotBandColors;
}

/**
 * Tiny helper to return the appropriate height for everything that should not overlap the capsule lane.
 *
 * @returns the height in pixel that is available
 */
export function getChartSeriesDisplayHeight(options: { chart: Highcharts.Chart; capsuleLaneHeight: number }): number {
  return options.chart.plotHeight - options.capsuleLaneHeight;
}

/**
 * This function manages the axis offsets and, if addAxisTitle is true, the display of axis labels.
 * Conceptually this function:
 *  - iterates over all the displayed lane
 *  - then iterates over all the series assigned to each lane
 *  - adjust the offset for each series axis so that the labels do not overlap
 *  - keeps track of the biggest offset of any given axis so that the overall chart margins can be set accordingly.
 */
export function manageAxisOffsets(options: {
  addAxisTitle: boolean;
  items: any[];
  isCapsuleTime: boolean;
  chart: Highcharts.Chart;
  capsuleLaneHeight: number;
  sqTrendStore: TrendDataForChart;
  skipAxisUpdate?: boolean;
}): { offsetLeft: number; offsetRight: number } {
  const { addAxisTitle, items, chart, skipAxisUpdate = false, sqTrendStore } = options;

  let largestOffsetLeft = 0;
  let largestOffsetRight = 0;

  const lanes = chartLanes.getDisplayedLanes(items);
  const laneCount = lanes.length;

  _.forEach(lanes, (lane) => {
    const { height: laneHeight } = chartLanes.computeLaneValues(lane, options);

    // Find all the items that are in the current lane.
    const itemsInLane = _.filter(items, (item: any) => {
      return (
        (item.itemType === ITEM_TYPES.SERIES ||
          item.itemType === ITEM_TYPES.SCALAR ||
          item.itemType === ITEM_TYPES.CONDITION) &&
        _.get(item, 'lane') === lane
      );
    });
    // Find all the unique axis that belong to this lane:
    const uniqueAxisAssignmentInLane = _.chain(itemsInLane).map('axisAlign').uniq().value();
    let offsetLeft = 0;
    let offsetRight = 0;
    const padding = addAxisTitle ? 30 : 10;

    _.forEach(_.sortBy(uniqueAxisAssignmentInLane), (assignment) => {
      const seriesSharingAnAxis = _.filter(itemsInLane, {
        axisAlign: assignment,
      });
      _.forEach(seriesSharingAnAxis, (series) => {
        const axis = getItemYAxis(chart, series);
        if (!axis || !_.get(axis, 'visible', false)) {
          return;
        }
        const tickPositions =
          getNumericTickPositionsSqLabel(axis.userMin, axis.userMax, series, laneHeight, laneCount) ?? [];

        let axisUpdates = {
          title: {
            useHTML: true,
            enabled: false,
            text: '',
            style: {},
          },
        };

        if (addAxisTitle) {
          const axisTitle = getAxisDisplayText(
            items,
            assignment,
            seriesSharingAnAxis,
            axis.height ? axis.height : laneHeight,
            sqTrendStore,
          );

          axisUpdates = {
            title: {
              ...getAxisTitleOptions(axisTitle, axis.opposite),
              enabled: true,
            },
          };
        }

        const labelLength = getLabelWidth(tickPositions, axis, laneHeight, padding, series.isStringSeries, series);

        const opposite = axis.userOptions.opposite;
        if (!skipAxisUpdate) {
          setAxisProperties(
            axis,
            _.assign(axisUpdates, { offset: opposite ? offsetRight : offsetLeft }, { axisWidth: labelLength }),
          );
        }

        if (opposite) {
          offsetRight += labelLength;
          largestOffsetRight = _.max([offsetRight, largestOffsetRight]) ?? largestOffsetRight;
        } else {
          offsetLeft += labelLength;
          largestOffsetLeft = _.max([offsetLeft, largestOffsetLeft]) ?? largestOffsetLeft;
        }
      });
    });
  });

  return { offsetLeft: largestOffsetLeft, offsetRight: largestOffsetRight };
}

/**
 * This function renders the "Lane Label" for signals and conditions.
 */
export function manageLaneLabelDisplay(options: {
  chart: Highcharts.Chart;
  items: any[];
  sqTrendStore: TrendDataForChart;
  conditionValues: ConditionValuesForLabels;
}) {
  const { chart, items, sqTrendStore, conditionValues } = options;
  const lanes = chartLanes.getDisplayedLanes(items);
  const labelAxis = _.find(chart.yAxis, {
    userOptions: { id: PLOT_BAND_AXIS_ID },
  });

  if (!labelAxis || !labelAxis.userOptions?.plotBands) {
    return;
  }

  const plotBandOptions = labelAxis.userOptions.plotBands as Highcharts.XAxisPlotBandsOptions[];
  labelAxis.userOptions.plotBands = plotBandOptions;

  let shouldUpdateLabelAxis = false;
  lanes.forEach((lane, idx) => {
    // Set the lane display labels, odd plotLinesAndBands are spacers so multiply by 2
    const plotBand = plotBandOptions[idx * 2];
    if (plotBand?.label) {
      const labelText = getLaneDisplayText(items, lane, sqTrendStore, conditionValues);
      plotBand.label.text = labelText;
      shouldUpdateLabelAxis = true;
    }
  });

  if (shouldUpdateLabelAxis) {
    labelAxis.update(
      {
        ...labelAxis.userOptions,
        plotBands: plotBandOptions,
      },
      false,
    );
  }
}

/**
 * Updates the capsule time y-axis tick positions. It does not redraw the chart.
 */
export function updateYAxisAlignment(options: {
  chart: Highcharts.Chart;
  isCapsuleTime: boolean;
  items: { id: string }[];
}) {
  const { chart, isCapsuleTime, items } = options;
  if (isCapsuleTime) {
    // Iterate through and update all y axes
    _.forEach(chart.yAxis, (axis: any) => {
      // We don't want to update the plot band here
      if (axis.options.id === PLOT_BAND_AXIS_ID) {
        return;
      }

      const item = getAxisItem(items, axis);
      if (!item) {
        return;
      }

      axis.update(
        {
          tickPositions: item.isStringSeries ? _.map(item.stringEnum, 'key').sort() : undefined,
          labels: { enabled: true },
          startOnTick: false,
          endOnTick: false,
        },
        false,
      );
    });
  }
}

//endregion

export function getYAxisIdFromItem(item: { id: string }) {
  return `yAxis-${item.id}`;
}

/**
 * Gets the item associated with an axis
 *
 * @param items - The items to search through
 * @param axis - The axis
 * @returns The requested item; or undefined if not found.
 */
export function getAxisItem(items: { id: string }[], axis: Highcharts.Axis): any {
  return _.find(items, (item) => axis.series && axis.series[0] && axis.series[0].userOptions.id === item.id);
}

/**
 * Updates the y-axis extremes of the corresponding series. Does not redraw the chart.
 */
export function updateSeriesYExtremes(options: { items: readonly any[]; chart: Highcharts.Chart }) {
  const { items, chart } = options;
  _.chain(items)
    .filter('yAxisConfig')
    .forEach((item: any) => {
      const axis = getItemYAxis(chart, item);
      if (!axis) {
        return;
      }
      if (axis.logarithmic) {
        axis.setExtremes(Number(item.yAxisMin), Number(item.yAxisMax), false, false);
      } else {
        axis.setExtremes(item.yAxisConfig.min, item.yAxisConfig.max, false, false);
      }
    })
    .value();
}

/**
 * Get the y-axis ticks for an axis based on the items min/max to ensure y-axis labels
 * are only shown within the axis' range
 *
 * @returns an Array of tick positions.
 */
export function getNumericTickPositions(options: ChartLaneOptions & ChartGetters): () => number[] {
  return function (this: Highcharts.Axis) {
    const chart = options.getChart();
    const items = options.getItems();
    const item = getAxisItem(items, this);
    if (!chart || !item) {
      return [];
    }

    const laneOptions: ChartLaneOptions = {
      chart,
      items,
      isCapsuleTime: options.isCapsuleTime,
      capsuleLaneHeight: options.capsuleLaneHeight,
    };
    const laneCount = _.get(chartLanes.getDisplayedLanes(items), 'length', 1);
    const { height: laneHeight } = chartLanes.computeLaneValues(item.lane, laneOptions);

    const min = item?.yAxisConfig?.min ?? this.min ?? 0;
    const max = item?.yAxisConfig?.max ?? this.max ?? 0;

    return getNumericTickPositionsSqLabel(min, max, item, laneHeight, laneCount) ?? [];
  };
}

/**
 * Formats the y-axis labels.
 */
export function yAxisFormatter(options: ChartLaneOptions & ChartGetters): () => string {
  return function (this: Highcharts.AxisLabelsFormatterContextObject) {
    const chart = options.getChart();
    const items = options.getItems();
    if (!chart) {
      return '';
    }

    const laneOptions: ChartLaneOptions = {
      chart,
      items,
      isCapsuleTime: options.isCapsuleTime,
      capsuleLaneHeight: options.capsuleLaneHeight,
    };
    const laneHeight = chartLanes.getSignalLaneHeight(laneOptions);
    const axisUserOptions = this.axis.userOptions as Highcharts.AxisOptions & { formatOptions?: any };
    const formattedValue = typeof this.value === 'number' ? this.value : Number(this.value);

    return formatYAxisTick(
      formattedValue,
      fetchTickAttributes(Number(this.axis.min), Number(this.axis.max), axisUserOptions.formatOptions, laneHeight),
    );
  };
}

/**
 * Formats the y-axis string labels.
 */
export function yAxisStringFormatter(options: { getItems: () => any[] }): () => string {
  return function (this: Highcharts.AxisLabelsFormatterContextObject) {
    // this.axis.series[0].options.stringEnum does not get updated when the series updates, therefore get it
    // from items
    const series = _.find(options.getItems(), (item) => item.id === this.axis.series[0]?.options.id);
    if (!series) {
      return '';
    }
    const axisUserOptions = this.axis.userOptions as Highcharts.AxisOptions & { formatOptions?: any };
    const formattedValue = typeof this.value === 'number' ? this.value : Number(this.value);
    return formatStringYAxisLabel(formattedValue, series, axisUserOptions.formatOptions?.stringFormat);
  };
}

export function updateYAxisPropertiesAndLabels(
  options: ChartLaneOptions & {
    sqTrendStore: TrendDataForChart;
    conditionValues: ConditionValuesForLabels;
    capsuleTimeColorMode: CapsuleTimeColorMode;
    axisTitlePresent: boolean;
    labelDisplayConfiguration: LabelDisplayConfiguration;
    darkMode?: boolean;
  },
) {
  const colors = processYAxisChangesPerLane({
    ...options,
    useDefaultColor: true,
  });
  updatePlotBands({ ...options, colors });
  manageAxisOffsets({
    ...options,
    addAxisTitle: options.axisTitlePresent,
  });
  manageLaneLabelDisplay(options);
}

/**
 * Toggles between line and point display.
 *
 * @param options.items - An Array of store items that have changed.
 */
export function setPointsOnly(options: { items: any[]; chart: Highcharts.Chart; isCapsuleTime: boolean }) {
  const { items, isCapsuleTime, chart } = options;
  const chartSeries = chart.series;
  _.forEach(items, (item: any) => {
    const seriesArray = _.filter(chartSeries, (series: any) => {
      return isCapsuleTime ? series.userOptions.isChildOf === item.isChildOf : series.userOptions.id === item.id;
    }) as any[];

    _.forEach(seriesArray, (series) => {
      if (item.sampleDisplayOption === SAMPLE_OPTIONS.BAR) {
        _.assign(series.options, BAR_CHART_ESSENTIALS, {
          pointWidth: item.lineWidth,
        });
      } else if (_.includes(CAPSULE_TYPES, item.itemType)) {
        return; // do not set options for capsule series
      } else {
        series.options.type = 'line';
        if (item.sampleDisplayOption !== SAMPLE_OPTIONS.LINE) {
          if (item.sampleDisplayOption === SAMPLE_OPTIONS.LINE_AND_SAMPLE) {
            series.options.lineWidth = 0.5;
          } else {
            series.options.lineWidth = 0;
          }

          const radius = item.lineWidth + 1;
          series.options.marker = { ...ENABLED_MARKER, radius };
        } else {
          const defaultLineWidth = LINE_WIDTHS[item.itemType as keyof typeof LINE_WIDTHS] ?? 0;
          series.options.lineWidth = item.lineWidth || defaultLineWidth;
          series.options.marker = DISABLED_MARKER;
        }
      }
      series.update(series.options, false);
    });
  });
}

type CapsuleLabelContext = {
  point: Highcharts.Point & {
    dataLabelString?: string;
    nextPointStart?: number;
    clientX?: number;
  };
  series: Highcharts.Series & { userOptions?: any; options: any; chart: Highcharts.Chart };
};

/**
 * Formats the capsule labels
 */
export function formatCapsuleLabel(this: CapsuleLabelContext): string | null {
  const zeroLength = this.series.userOptions?.sampleDisplayOption === SAMPLE_OPTIONS.SAMPLES;

  // zeroLength capsules have 2 overlapping data points. We need to make sure we get the "correct" aka "first"
  // point to ensure the following logic works as expected.
  const point = (!zeroLength ? this.point : _.first(_.filter(this.series.data, { clientX: this.point.clientX }))) as
    | CapsuleLabelContext['point']
    | undefined;

  if (!point?.dataLabelString) {
    return null;
  }
  const pointPlotX = point.plotX ?? 0;
  let label = point.dataLabelString;
  let width: number | null = null;
  // zeroLength capsules are placed on a separate series to ensure the marker property doesn't cause unwanted
  // artifacts (https://seeq.atlassian.net/browse/CRAB-37825), but because of that the "next" point is often not the
  // actual next point, so instead of taking the next data point we use the nextPoint property (that is set in the
  // trendCapsule.store) to determine the max width of the label.
  if (zeroLength) {
    if (point.nextPointStart) {
      const axis = this.series.chart.xAxis[0];
      width = Math.floor((axis.toPixels(point.nextPointStart) ?? 0) - (axis.toPixels(point.x) ?? 0));
    }
  } else {
    // Constrain the width to ensure long labels don't run outside the capsule
    const nextPoint = _.find<Highcharts.Point>(this.series.data, {
      index: point.index + 1,
    });

    const nextPointPlotX = nextPoint?.plotX ?? pointPlotX;
    width = Math.floor(nextPointPlotX - pointPlotX);
  }

  const overlay = this.series.userOptions?.overlay;
  let maxWidth = '';
  if (overlay) {
    const getMaxWidthValue = () => {
      const seriesLane = this.series.userOptions?.lane;
      const allLaneSeriesWithGreaterOverlay = this.series.chart?.series?.filter(
        ({ userOptions }: Highcharts.Series) =>
          (userOptions as any)?.lane === seriesLane && (userOptions as any)?.overlay > overlay,
      );
      if (!allLaneSeriesWithGreaterOverlay?.length) {
        return;
      }

      const pointX = point.x;

      const allOverlayedData = _.flatMap(allLaneSeriesWithGreaterOverlay, 'userOptions.data');
      for (let i = 0; i < allOverlayedData.length; i++) {
        const dataX = allOverlayedData[i].x;
        const nextDataX = allOverlayedData[i + 1]?.x;
        if (pointX >= dataX && pointX <= nextDataX) {
          return 0;
        }
      }

      const nextOverlayPoint = _.chain(allOverlayedData)
        .filter(({ dataLabelString, x }) => dataLabelString && x > pointX)
        .sortBy('x')
        .head()
        .value();

      if (nextOverlayPoint) {
        const plotX = (this.series.xAxis?.toPixels(nextOverlayPoint.x) ?? 0) - this.series.chart.plotLeft;

        return Math.floor(plotX - pointPlotX) - 1;
      }
    };

    const maxWidthValue = getMaxWidthValue();
    if (!_.isNil(maxWidthValue)) {
      maxWidth = ` max-width: ${maxWidthValue > 1 ? maxWidthValue : 0}px`;
    }
  }

  const hasUncertainCapsuleSeries = this.series.chart?.series?.some(
    ({ options }: Highcharts.Series) => (options as any)?.isUncertain,
  );
  const isFullyUncertainCapsule =
    hasUncertainCapsuleSeries && (point.options?.x ?? 0) >= this.series.options.firstFullyUncertainCapsuleStart;

  const widthValue = typeof width === 'number' ? width : null;
  const widthStyle = widthValue !== null && widthValue > 0 ? `width: ${widthValue}px` : '';
  const pointColor: Highcharts.ColorType | string = (point.color ?? '#000') as Highcharts.ColorType | string;
  const resolvedPointColor = typeof pointColor === 'string' ? pointColor : (pointColor?.toString?.() ?? '#000');
  const contrastColor = tinycolor(resolvedPointColor).isDark() ? '#fff' : '#000';

  const colorStyle = zeroLength
    ? `color: ${resolvedPointColor}`
    : `color: ${isFullyUncertainCapsule ? '#000' : contrastColor}`;

  return `<span style="${colorStyle}; ${widthStyle};${maxWidth}">${label}</span>`;
}

/**
 * Formats the label text for a point on a signal
 *
 * @param yValue - The Y-value of the point
 * @param signal - The signal from which the point comes
 * @param includeUnitOfMeasure - Whether to append the unit of measure to numeric values
 * @param useStringFormatter - Whether to format string values with the formatter
 */
export function formatPointLabel(
  yValue: number | string,
  signal: {
    isStringSeries?: boolean;
    stringEnum?: StringEnum[];
    formatOptions?: FormatOptions & { stringFormat?: string };
    unitOfMeasure?: UnitOfMeasure;
  },
  includeUnitOfMeasure = true,
  useStringFormatter = false,
): string {
  if (signal.isStringSeries) {
    const stringValue = (signal.stringEnum ?? []).find(({ key }) => key === yValue)?.stringValue ?? '';
    return useStringFormatter
      ? formatString(stringValue, {
          format: signal.formatOptions?.stringFormat,
        })
      : stringValue;
  }

  let value = _.isFinite(yValue) ? formatNumber(yValue, signal.formatOptions) : yValue.toString();
  const resolvedUnit = signal.unitOfMeasure;
  if (includeUnitOfMeasure && resolvedUnit && resolvedUnit.value !== '') {
    value += ` ${
      resolvedUnit.fromSourceUom || resolvedUnit.isDisplay ? `<em>${resolvedUnit.value}</em>` : resolvedUnit.value
    }`;
  }
  return value;
}

/**
 * Returns an offset for conditions in the same lane if capsules overlap
 */
export function getCapsuleRowOffset(
  lane: number,
  capsuleRows: any[],
  allLaneData: ConditionLaneData,
  laneHasStacking: boolean,
): number {
  const capsules = _.flatMap(capsuleRows, 'capsules').filter(Boolean);

  if (!allLaneData.has(lane)) {
    if (!lane) {
      return 0;
    }
    allLaneData.set(lane, { nextOffset: 0, capsulesMap: new Map([[0, capsules]]) });
    return 0;
  }

  const laneData = allLaneData.get(lane);
  if (!laneData) {
    return 0;
  }

  const offsets = [...laneData.capsulesMap.keys()];
  let offsetIndex = 0;
  let offset = offsets[offsetIndex] ?? -1;
  for (let i = 0; i < capsules.length && !laneHasStacking; i++) {
    const capsule = capsules[i];

    while (offsetIndex < offsets.length) {
      offset = offsets[offsetIndex];
      const offsetCapsules = (laneData.capsulesMap.get(offset) ?? []).filter(Boolean);
      const isOverlapping = _.some(
        offsetCapsules,
        (c) => capsule.startTime < c.endTime && capsule.endTime > c.startTime,
      );
      if (!isOverlapping) {
        break;
      }

      offsetIndex++;
    }

    if (offsetIndex === offsets.length) {
      offset = laneData.nextOffset;
      break;
    }
  }

  const existingCapsules = (laneData.capsulesMap.get(offset) ?? []).filter(Boolean);
  laneData.capsulesMap.set(offset, _.concat(capsules, existingCapsules));

  return offset;
}

function findUncertainCapsules(items: any[], conditionId: string): any[] {
  return _.chain(items)
    .filter(
      ({ conditionId: id, itemType }) =>
        getCertainId(id) === conditionId && _.includes(UNCERTAIN_CAPSULE_TYPES, itemType),
    )
    .value();
}

/**
 * Each capsule series has it's own y-axis.
 *
 * If lane labels are shown the buffer for each axis is increased to ensure the label can be displayed without
 * interfering with the capsule display.
 *
 * @return The new capsule lane height
 */
export function updateCapsuleAxis(options: {
  showCapsuleLaneLabels: boolean;
  capsuleLaneHeight: number;
  items: any[];
  chart: Highcharts.Chart;
  sqTrendStoreData: TrendDataForChart;
  lanes: number[];
  isCapsuleTime: boolean;
  colors: any;
  startingTop?: number;
  labelDisplayConfiguration: LabelDisplayConfiguration;
  darkMode?: boolean;
  /** Called with the names of conditions whose capsule data was incomplete (empty array if none were). */
  onDroppedConditionData?: (conditionNames: string[]) => void;
}): number {
  const {
    showCapsuleLaneLabels,
    capsuleLaneHeight,
    items,
    chart,
    sqTrendStoreData,
    startingTop = 0,
    onDroppedConditionData,
  } = options;
  const droppedConditionNames = new Set<string>();

  let nextTop = startingTop;
  const hasTopBuffer = showCapsuleLaneLabels || sqTrendStoreData.customizationMode !== CUSTOMIZATION_MODES.OFF;
  const extraTopBuffers = new Map([[-1, hasTopBuffer ? laneLabelHeight() : 0]]);

  const allLaneData: ConditionLaneData = new Map();
  const conditionAxisAndOffsets: ConditionAxisAndOffsets = new Map();
  const conditionsInSignalLane: AdjustableConditions = new Map();
  const conditionsToExpand: AdjustableConditions = new Map();
  const maxOverlayPerLane = new Map<number, number>();
  const orderInLaneHeights = new Map<string, number>();
  const orderInLaneData = new Map<string, { lane: number; orderInLane: number; extraTopOffset: number }>();
  const extraHeight = chartLanes.computeConditionLaneExtraHeight(options);
  const capsules = _.filter(items, { itemType: ITEM_TYPES.CAPSULE });
  const lanesWithOrderInLane = new Set(
    capsules
      .filter((item) => _.isFinite(item.orderInLane))
      .map((item) => item.lane)
      .filter((lane): lane is number => lane !== undefined),
  );
  const hasOrderInLane = capsules.some((capsule) => _.isFinite(capsule.orderInLane));
  const shouldExpandCapsules = sqTrendStoreData.canCapsulesExpand && extraHeight > 0 && !hasOrderInLane;

  const maxOverlay = items.reduce((max, item) => Math.max(max, item.overlay ?? 0), 0);
  const anyCapsulesSelected = capsules.filter((row) => row.anyCapsulesSelected).length > 0;

  const conditionsInLaneWithoutCapsulesCount = _.chain(capsules)
    .groupBy('lane')
    .mapValues((capsules) =>
      _.chain(capsules)
        .groupBy('conditionId')
        .filter((capsuleRows) => !capsuleRows.some((capsuleRow) => capsuleRow.capsules?.length > 0))
        .size()
        .value(),
    )
    .value();

  _.chain(capsules)
    .groupBy('conditionId')
    .entries()
    .sortBy(([conditionId]) => {
      const item = _.find(items, { conditionId });
      return item?.orderInLane ?? Infinity;
    })
    .map((entry) => entry as [string, any[]])
    .forEach(([conditionId, rawCapsuleRows]) => {
      const axisForCapsuleRow = _.find(chart.yAxis, {
        userOptions: { id: getCapsuleAxisId(conditionId) },
      });

      if (axisForCapsuleRow) {
        const conditionItem = _.find(items, { conditionId });

        if (rawCapsuleRows.some((capsuleRow) => !capsuleRow.capsules)) {
          droppedConditionNames.add(rawCapsuleRows[0]?.conditionName ?? conditionId);
        }

        const overlay = conditionItem?.overlay;
        const orderInLane = conditionItem?.orderInLane;
        const hasOverlay = _.isFinite(overlay);
        const hasOrderInLane = _.isFinite(orderInLane);
        const overlayHeight = hasOverlay ? (maxOverlay - overlay) * OVERLAY_LINE_WIDTH : 0;
        let nonZeroLengthCapsuleIndex = 0;

        const capsuleRows = !hasOverlay
          ? rawCapsuleRows
          : rawCapsuleRows.map((capsuleRow, index) => {
              if (capsuleRow.isZeroLength) {
                return capsuleRow;
              }

              const lineWidth = hasOverlay ? capsuleRow.lineWidth + overlayHeight : capsuleRow.lineWidth;
              const isNextOffset = nonZeroLengthCapsuleIndex > 0;
              const yValue = isNextOffset ? nonZeroLengthCapsuleIndex * lineWidth : capsuleRow.yValue;
              const data = isNextOffset
                ? capsuleRow.data.map((point: any) => ({
                    ...point,
                    y: point.y && yValue,
                  }))
                : capsuleRow.data;

              if (rawCapsuleRows[index + 1]?.yValue !== capsuleRow.yValue) {
                nonZeroLengthCapsuleIndex++;
              }

              const selectedCapsuleInSignalLane =
                capsuleRow.anyCapsulesSelected && chartLanes.hasSignalsInLane(capsuleRow.lane, items);

              return {
                ...capsuleRow,
                lineWidth,
                data,
                yValue,
                zIndex: selectedCapsuleInSignalLane ? -(maxOverlay - (overlay as number) + 1) : overlay,
              };
            });

        const maxYValue = _.chain(capsuleRows).map('yValue').max().value() ?? 0;
        const minYValue = _.chain(capsuleRows).map('yValue').min().value() ?? 0;
        const conditionLane = _.find(items, { id: capsuleRows[0]?.id })?.lane;
        const laneHasOrderInLane = lanesWithOrderInLane.has(conditionLane);
        // When a lane uses orderInLane, conditions normally render separately, except during
        // transitions when a condition may temporarily have both overlay and orderInLane set
        const canRenderInSignalLane = laneHasOrderInLane ? hasOverlay : true;
        if (
          !shouldExpandCapsules &&
          canRenderInSignalLane &&
          !hasOrderInLane &&
          chartLanes.hasSignalsInLane(conditionLane as number, items)
        ) {
          const uncertainCapsules = findUncertainCapsules(items, conditionId);
          updateSeries(chart, _.concat(capsuleRows, uncertainCapsules), false, anyCapsulesSelected ? ['color'] : []);

          conditionsInSignalLane.set(conditionId, {
            maxYValue,
            minYValue,
            lane: conditionLane as number,
            axis: axisForCapsuleRow,
            rowOffset: 0,
            overlay,
            orderInLane,
            conditionsWithoutCapsulesCount: conditionsInLaneWithoutCapsulesCount[conditionLane as number] ?? 0,
          });

          return;
        }

        if (hasOverlay) {
          const currentMax = maxOverlayPerLane.get(conditionLane as number) ?? 0;
          maxOverlayPerLane.set(conditionLane as number, Math.max(currentMax, overlay));
        }

        const lineWidth = _.chain(capsuleRows).map('lineWidth').max().value() ?? 0;
        const buffer = lineWidth / 2;

        const numOffsets = allLaneData.get(conditionLane as number)?.capsulesMap.size ?? 0;
        // For order-in-lane only use stacking mode if there are signals in the lane.
        // When there are only conditions, let getCapsuleRowOffset do normal overlap detection.
        const laneHasStacking =
          hasOverlay || (hasOrderInLane && chartLanes.hasSignalsInLane(conditionLane as number, items));
        const rowOffset = getCapsuleRowOffset(conditionLane as number, capsuleRows, allLaneData, laneHasStacking);
        const min = minYValue - buffer;
        const max = maxYValue + buffer;
        const height = max - min;

        const hasCustomLabelInLane = options.labelDisplayConfiguration.customLabels.some(
          (label) => label.location === 'lane' && label.target === (conditionLane as number),
        );
        const laneTopBuffer = hasCustomLabelInLane ? laneLabelHeight() : (extraTopBuffers.get(-1) ?? 0);
        extraTopBuffers.set(conditionLane as number, laneTopBuffer);
        const extraTopOffset = chartLanes.extraTopOffset(rowOffset, laneTopBuffer);

        if (hasOrderInLane) {
          orderInLaneHeights.set(conditionId, height);
          orderInLaneData.set(conditionId, { lane: conditionLane as number, orderInLane, extraTopOffset });
        }

        const laneData = allLaneData.get(conditionLane as number);
        if (laneData) {
          if (laneData.capsulesMap.size > numOffsets) {
            laneData.nextOffset += height + extraTopOffset;
          } else {
            laneData.nextOffset = Math.max(laneData.nextOffset, height + extraTopOffset);
          }
        }

        if (!capsuleLaneHeight) {
          conditionAxisAndOffsets.set(conditionId, {
            rowOffset,
            axis: axisForCapsuleRow,
            lane: conditionLane as number,
            overlay,
            orderInLane,
            conditionsWithoutCapsulesCount: conditionsInLaneWithoutCapsulesCount[conditionLane as number] ?? 0,
          });
        }

        if (shouldExpandCapsules) {
          conditionsToExpand.set(conditionId, {
            maxYValue,
            minYValue,
            lane: conditionLane as number,
            axis: axisForCapsuleRow,
            lineWidth,
            numCapsuleRows: _.chain(capsuleRows).reject('isZeroLength').map('yValue').uniq().size().value() || 1,
            rowOffset,
            overlay,
            orderInLane,
          });

          return;
        }

        const adjustedConditions = chartLanes.getAdjustedConditions(conditionId);
        if (adjustedConditions || hasOverlay) {
          const uncertainCapsules = findUncertainCapsules(items, conditionId);
          updateSeries(chart, _.concat(capsuleRows, uncertainCapsules), false, anyCapsulesSelected ? ['color'] : []);

          if (adjustedConditions) {
            adjustedConditions.delete(conditionId);
          }
        }

        axisForCapsuleRow.update(
          {
            height,
            top: capsuleLaneHeight > 0 ? nextTop : undefined,
            min,
            max,
          },
          false,
        );

        if (!_.isFinite(height)) {
          throw new Error(`Capsule lane height of ${height} is not a finite number`);
        }

        nextTop += height;
      }
    })
    .value();

  onDroppedConditionData?.([...droppedConditionNames]);

  maxOverlayPerLane.forEach((maxLaneOverlay, lane) => {
    const laneData = allLaneData.get(lane);
    // Only add overlay stacking height when the lane has no signals
    if (laneData && maxLaneOverlay > 1 && !chartLanes.hasSignalsInLane(lane, items)) {
      const conditionsWithoutCapsules = conditionsInLaneWithoutCapsulesCount[lane] ?? 0;
      const overlayStackingHeight = ((maxLaneOverlay - conditionsWithoutCapsules - 1) * OVERLAY_LINE_WIDTH) / 2;
      laneData.nextOffset += overlayStackingHeight;
    }
  });

  const heightPerLane = chartLanes.getAllocatedLaneHeight(
    allLaneData,
    items,
    options,
    conditionsInSignalLane,
    extraTopBuffers,
  );

  const conditionsByLane = new Map<
    number,
    Array<{ conditionId: string; orderInLane: number; height: number; extraTopOffset: number }>
  >();
  orderInLaneData.forEach(({ lane, orderInLane, extraTopOffset }, conditionId) => {
    if (!conditionsByLane.has(lane)) {
      conditionsByLane.set(lane, []);
    }
    conditionsByLane.get(lane)!.push({
      conditionId,
      orderInLane,
      height: orderInLaneHeights.get(conditionId) || 0,
      extraTopOffset,
    });
  });
  conditionsByLane.forEach((conditions, lane) => {
    conditions.sort((a, b) => a.orderInLane - b.orderInLane);

    const labelHeight = extraTopBuffers.get(lane) ?? 0;
    const availableHeight = heightPerLane > 0 ? heightPerLane - labelHeight : 0;
    const numConditions = conditions.length;
    const expandedHeight = heightPerLane > 0 && numConditions > 0 ? availableHeight / numConditions : 0;

    let cumulativeOffset = 0;
    conditions.forEach(({ conditionId, height, extraTopOffset }) => {
      const existing = conditionAxisAndOffsets.get(conditionId);
      if (existing) {
        existing.rowOffset = cumulativeOffset;
        if (expandedHeight > 0) {
          updateCapsuleHeightForCondition(chart, conditionId, existing, expandedHeight);
        }
      }
      const heightToUse = expandedHeight > 0 ? expandedHeight : height;
      const heightToAdd = cumulativeOffset === 0 ? heightToUse + extraTopOffset : heightToUse;
      cumulativeOffset += heightToAdd;
    });

    const laneData = allLaneData.get(lane);
    if (laneData) {
      laneData.nextOffset = Math.max(laneData.nextOffset, cumulativeOffset);
    }
  });

  // Expand non-orderInLane (overlay) lanes to their allocated height
  if (heightPerLane > 0) {
    allLaneData.forEach((laneData, lane) => {
      if (conditionsByLane.has(lane)) {
        return; // Already handled by orderInLane logic
      }

      // Find all conditions in this lane with their stacking info
      const conditionsInLane: Array<{ conditionId: string; rowOffset: number }> = [];
      conditionAxisAndOffsets.forEach((data, conditionId) => {
        if (data.lane === lane && !_.isFinite(data.orderInLane)) {
          conditionsInLane.push({
            conditionId,
            rowOffset: data.rowOffset,
          });
        }
      });

      if (conditionsInLane.length === 0) {
        return;
      }

      const labelHeight = extraTopBuffers.get(lane) ?? 0;
      const availableHeightForLane = heightPerLane - labelHeight;

      // Group conditions by rowOffset to find unique stacking levels
      const uniqueOffsets = _.uniq(conditionsInLane.map((c) => c.rowOffset)).sort((a, b) => a - b);
      const numLevels = uniqueOffsets.length;
      const heightPerLevel = numLevels > 0 ? availableHeightForLane / numLevels : 0;

      // Map each rowOffset to a stacking level index
      const offsetToLevel = new Map<number, number>();
      uniqueOffsets.forEach((offset, index) => {
        offsetToLevel.set(offset, index);
      });

      const maxOverlayPerRowOffset = new Map<number, number>();
      conditionsInLane.forEach(({ conditionId, rowOffset }) => {
        const overlay = conditionAxisAndOffsets.get(conditionId)?.overlay ?? 1;
        maxOverlayPerRowOffset.set(rowOffset, Math.max(maxOverlayPerRowOffset.get(rowOffset) ?? 0, overlay));
      });

      // Update all capsules: wedding-cake overlay layout per stacking level
      conditionsInLane.forEach(({ conditionId, rowOffset }) => {
        const axisData = conditionAxisAndOffsets.get(conditionId);
        if (!axisData) {
          return;
        }

        const level = offsetToLevel.get(rowOffset) ?? 0;
        const overlay = axisData.overlay || 1;
        const conditionsWithoutCapsulesCount = axisData.conditionsWithoutCapsulesCount || 0;
        const maxOverlayInRow = maxOverlayPerRowOffset.get(rowOffset) ?? overlay;
        const { capsuleHeight, capsuleTopInset, overlayLineWidth } = computeOverlayLayoutForLevel({
          heightPerLevel,
          overlay,
          maxOverlayInRow,
          conditionsWithoutCapsulesCount,
        });

        const capsuleTop = labelHeight + level * heightPerLevel + capsuleTopInset;
        const newRowOffset = calculateRowOffsetForExpandedCapsule(
          capsuleTop,
          rowOffset,
          lane,
          overlay,
          conditionsWithoutCapsulesCount,
          extraTopBuffers,
          overlayLineWidth,
        );

        axisData.overlayLineWidth = overlayLineWidth;
        axisData.rowOffset = newRowOffset;
        updateCapsuleHeightForCondition(chart, conditionId, axisData, capsuleHeight);
      });

      laneData.nextOffset = heightPerLane;
    });
  }

  chartLanes.updateConditionLaneHeights(allLaneData);

  let adjustableConditions = conditionsToExpand;
  if (!shouldExpandCapsules) {
    chartLanes.resetExtraConditionLaneHeight();
    adjustableConditions = conditionsInSignalLane;
  } else {
    conditionsToExpand.forEach((conditionData, conditionId) => {
      const updatedData = conditionAxisAndOffsets.get(conditionId);
      if (updatedData && _.isFinite(updatedData.orderInLane)) {
        conditionData.rowOffset = updatedData.rowOffset;
      }
    });
  }

  chartLanes.adjustCapsulesLineWidth(adjustableConditions, options, extraHeight);
  chartLanes.updateConditionOffsets(conditionAxisAndOffsets, options, extraTopBuffers);

  updatePlotBands(options);

  return nextTop;
}

/**
 * Gets the y-axis range - [max, min] - for each item
 *
 * @returns an object containing the item ids as keys and the y-axis ranges as values
 */
export function getItemRanges(items: any[]): Record<string, [number, number]> {
  return _.chain(items)
    .uniqBy((item) => (item.isStringSeries ? item.id : (item.interestId ?? item.id)))
    .reduce(
      (ranges: Record<string, [number, number]>, item) => {
        const itemId = item.isStringSeries ? item.id : (item.interestId ?? item.id);
        ranges[itemId] = [item.yAxisMax, item.yAxisMin];

        return ranges;
      },
      {} as Record<string, [number, number]>,
    )
    .value();
}

/**
 * Update capsule height for both axis and all series associated with a condition.
 */
function updateCapsuleHeightForCondition(
  chart: Highcharts.Chart,
  conditionId: string,
  axisData: { axis: Highcharts.Axis },
  height: number,
): void {
  axisData.axis.update({ height }, false);

  const allSeries = _.filter(chart.series, (s) => getCertainId(_.get(s, 'userOptions.conditionId')) === conditionId);
  allSeries.forEach((series) => {
    series.update({ lineWidth: height } as Highcharts.SeriesOptionsType, false);
  });
}

/**
 * Calculate the rowOffset value needed so that updateConditionOffsets produces the desired capsule position.
 *
 * updateConditionOffsets uses the formula: top = offset + rowOffset + extraTopOff + extraRowOff + overlayOffset
 * We want the capsule at position: offset + capsuleTop (where capsuleTop is relative to lane)
 * Therefore: rowOffset = capsuleTop - extraTopOff - extraRowOff - overlayOffset
 */
function calculateRowOffsetForExpandedCapsule(
  capsuleTop: number,
  rowOffset: number,
  lane: number,
  overlay: number,
  conditionsWithoutCapsulesCount: number,
  extraTopBuffers: Map<number, number>,
  overlayLineWidth = OVERLAY_LINE_WIDTH,
): number {
  const overlayOffset = getOverlayOffset(overlay, conditionsWithoutCapsulesCount, overlayLineWidth);
  const extraTopOff = chartLanes.extraTopOffset(rowOffset, extraTopBuffers.get(lane) ?? 0);
  const extraRowOff = chartLanes.extraRowOffset(lane, rowOffset);
  return capsuleTop - extraTopOff - extraRowOff - overlayOffset;
}

/**
 * Removes uncertainty indicators from the chart items.
 *
 * Condition chart items have an `isUncertain` property which can be used to remove all uncertain items.
 *
 * Signal chart items have the `zones` property set in {@link transformDataPoints} to specify regions of
 * uncertainty and the zones are removed here.
 *
 * @param items The chart items to remove all uncertainty indicators from.
 */
export function removeUncertaintyIndicatorsFromChartItems(items: any[]): any[] {
  return items
    .filter((item) => !item.isUncertain) // Filters out uncertain condition chart items
    .map((item) => {
      // Conditions have been taken care of by the above filter
      if (!_.isNil(item.conditionId)) {
        return item;
      }

      // Compare-view expects the chart items to be read-only
      return item.zones?.length ? Object.freeze({ ...item, zones: [] }) : item;
    });
}

/**
 * Updates the corresponding series with new options. Does not redraw the chart.
 *
 * @param items - The items with updated properties.
 * @param updateData - Specifies if series data should be updated or not. True by default
 */
export function updateSeries(
  chart: Highcharts.Chart,
  items: any[],
  updateData = true,
  extraPropertiesToOmit: string[] = [],
) {
  _.forEach(items, (item: any) => {
    const series = findChartSeries(chart, item.id);
    if (updateData) {
      updateChartSeriesData(series, item.data);
    }
    series.update(_.assign({}, _.omit(item, ['data'].concat(extraPropertiesToOmit))), false);
  });
}

/**
 * Updates the data points of the corresponding series. Does not redraw the chart.
 *
 * @param series - The series object
 * @param data - The data points for the series.
 */
function updateChartSeriesData(series: Highcharts.Series, data: any[]) {
  // SetData is expensive even if no data is present. Skip if the series has no data and we get an empty array
  if (_.isEmpty(series.data) && _.isEmpty(data)) {
    return;
  }

  // We call setData() separately from the update() call because passing false as the fourth argument to
  // setData() prevents Highcharts from trying to modify the data array we pass in. There's no corresponding
  // argument for update().
  series.setData(data, false, false, false);
}
